import React from 'react';

// ── Inline SVG: PDF file icon ──────────────────────────────────────────────
const PdfIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 16 16" fill="#dc3545">
        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
        <path d="M4.603 14.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.065.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.7 5.7 0 0 1-.911-.95 11.6 11.6 0 0 0-1.997.406 11.3 11.3 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.8.8 0 0 1-.58.029m1.379-1.901q-.25.115-.459.238c-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361q.016.032.026.044l.035-.012c.137-.056.355-.235.635-.572a8 8 0 0 0 .45-.606m1.64-1.33a13 13 0 0 1 1.01-.193 12 12 0 0 1-.51-.858 21 21 0 0 1-.5 1.05zm2.446.45q.226.245.435.41c.24.19.407.253.498.256a.1.1 0 0 0 .07-.015.3.3 0 0 0 .094-.12.44.44 0 0 0 .059-.2.1.1 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a4 4 0 0 0-.612-.059zm-3.423-2.979a2 2 0 0 0-.498-.156c-.08.053-.109.12-.123.188-.035.164-.016.395.05.661.037.135.086.285.145.438a4 4 0 0 0 .285-1.068.4.4 0 0 0 .14-.063z" />
    </svg>
);

// ── Inline SVG: Download arrow icon ────────────────────────────────────────
const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
    </svg>
);

// ── Report options ──────────────────────────────────────────────────────────
const REPORT_OPTIONS = [
    { key: 'pkl', label: 'PKL Report.pdf' },
    { key: 'ac', label: 'AC Report.pdf' },
    { key: 'mgirth', label: 'M-Girth PKL Report.pdf' },
];

/**
 * DownloadReportModal
 *
 * Props:
 *  - isOpen      {boolean}   – controls visibility
 *  - onClose     {function}  – called on backdrop click, × or Cancel
 *  - row         {object}    – the packing-list row (used for fileId badge & download context)
 *  - onDownload  {function}  – called with (reportKey, row) when a PDF card is clicked
 */
const DownloadReportModal = ({ isOpen, onClose, row, onDownload }) => {
    if (!isOpen) return null;

    const handleDownload = (key, label) => {
        if (typeof onDownload === 'function') {
            onDownload(key, row);
        } else {
            // Fallback: log until real API is wired
            console.log('Downloading:', label, row);
        }
    };

    return (
        <>
            {/* ── Backdrop ── */}
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1040 }}
                onClick={onClose}
            />

            {/* ── Modal ── */}
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1045 }}
                aria-modal="true"
                aria-labelledby="downloadReportModalLabel"
            >
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
                    <div className="modal-content border-0 shadow-lg rounded-3 overflow-hidden">

                        {/* Header */}
                        <div className="modal-header gtss-bg-primary text-white border-0 py-3 px-4">
                            <div className="d-flex align-items-center gap-2">
                                <DownloadIcon />
                                <h6 className="modal-title fw-bold mb-0" id="downloadReportModalLabel">
                                    Download Report
                                </h6>
                            </div>
                            {row?.fileId && (
                                <span
                                    className="ms-auto badge bg-white gtss-text-primary fw-semibold px-3 py-2 rounded-pill"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    {row.fileId}
                                </span>
                            )}
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-3"
                                aria-label="Close"
                                onClick={onClose}
                            />
                        </div>

                        {/* Body */}
                        <div className="modal-body px-4 py-4" style={{ background: '#f8f9fa' }}>
                            <p
                                className="text-muted small mb-3 fw-semibold text-uppercase"
                                style={{ letterSpacing: '0.05em' }}
                            >
                                Select a report to download
                            </p>
                            <div className="row g-3">
                                {REPORT_OPTIONS.map(({ key, label }) => (
                                    <div className="col-4" key={key}>
                                        <button
                                            type="button"
                                            className="btn w-100 h-100 d-flex flex-column align-items-center justify-content-between p-3 border rounded-3 bg-white"
                                            style={{
                                                minHeight: 130,
                                                gap: 10,
                                                transition: 'box-shadow 0.18s, transform 0.18s',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.13)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)';
                                                e.currentTarget.style.transform = '';
                                            }}
                                            onClick={() => handleDownload(key, label)}
                                        >
                                            <PdfIcon />
                                            <span
                                                className="w-100 text-center text-white fw-semibold px-2 py-1 rounded-2"
                                                style={{
                                                    fontSize: '0.7rem',
                                                    background: '#333',
                                                    lineHeight: 1.35,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {label}
                                            </span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-semibold px-4"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default DownloadReportModal;
